import type { OnboardingV1Step } from "@repo/types";

/**
 * Search validation for `/onboarding-v1-preview`, kept in its own module so
 * the eager route file can import it without reaching the preview fixture.
 *
 * The fixture builds a full onboarding data source and imports `viem` plus the
 * `~/features/start` barrel; `routes/onboarding-v1-preview.tsx` is an eager
 * route, so importing the normalizer from there pulled both into the
 * application ENTRY chunk for every visitor. Everything below is pure, and the
 * module has no cross-feature dependency at all: the step field is declared
 * against the shared `@repo/types` contract, which is the same contract
 * `OnboardingV1StepSearch` is built from.
 */
export interface OnboardingV1PreviewSearch {
  readonly art?: "cards";
  readonly chainId?: number;
  readonly country?: string;
  readonly step?: Exclude<OnboardingV1Step, "hook" | "name">;
  readonly controls?: false;
  readonly portfolioId?: string;
  readonly submitted?: true;
}

function readPositiveInteger(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }
  if (typeof value !== "string" || !/^\d+$/u.test(value)) {
    return null;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function readPortfolioId(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 && normalized.length <= 128 ? normalized : null;
}

export function normalizeOnboardingV1PreviewSearch(
  search: Record<string, unknown>,
): OnboardingV1PreviewSearch {
  const step = typeof search.step === "string" ? search.step : undefined;
  const normalizedStep = (
    [
      "hook",
      "name",
      "profile",
      "interests",
      "projection",
      "pick",
      "account",
      "fund",
    ] as const
  ).find((candidate) => candidate === step);
  const chainId = readPositiveInteger(search.chainId);
  const portfolioId = readPortfolioId(search.portfolioId);
  const country =
    typeof search.country === "string" &&
    /^[a-z]{2}$/iu.test(search.country.trim())
      ? search.country.trim().toUpperCase()
      : undefined;
  const controlsHidden =
    search.controls === false ||
    search.controls === 0 ||
    search.controls === "0" ||
    search.controls === "false";
  const submitted =
    search.submitted === true ||
    search.submitted === 1 ||
    search.submitted === "1" ||
    search.submitted === "true";

  return {
    ...(search.art === "cards" ? { art: "cards" as const } : {}),
    ...(normalizedStep && normalizedStep !== "hook" && normalizedStep !== "name"
      ? { step: normalizedStep }
      : {}),
    ...(chainId === null ? {} : { chainId }),
    ...(country ? { country } : {}),
    ...(controlsHidden ? { controls: false as const } : {}),
    ...(portfolioId === null ? {} : { portfolioId }),
    ...(submitted ? { submitted: true as const } : {}),
  };
}
